import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Badge, Card, Button } from 'react-bootstrap';
import { projectsApi } from '../services/api';
import { Project } from '../types';
import PageMeta from '../components/PageMeta';
import '../styles/pages.css';
import '../styles/ProjectsSection.css';

const ProjectDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [relatedProjects, setRelatedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [touchZoomed, setTouchZoomed] = useState(false);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (slug) {
      fetchProjectData(slug);
    }
  }, [slug]);

  const fetchProjectData = async (projectSlug: string) => {
    try {
      setLoading(true);
      const projectData = await projectsApi.getBySlug(projectSlug);

      if (!projectData) {
        navigate('/projetos');
        return;
      }

      setProject(projectData);

      // Define a imagem selecionada (primeira ou principal)
      if (projectData.galeriaFotos?.fotos?.length) {
        const mainPhoto = projectData.galeriaFotos.fotos.find(f => f.isPrincipal) || projectData.galeriaFotos.fotos[0];
        setSelectedImage(mainPhoto.url);
      }

      // Busca projetos relacionados (exclui o atual)
      const allProjects = await projectsApi.getAll();
      const related = allProjects
        .filter(p => p.slug !== projectSlug && p.categoriaLabel !== 'Dia de campo')
        .sort((a, b) => new Date(b.dataProjeto).getTime() - new Date(a.dataProjeto).getTime())
        .slice(0, 3);

      setRelatedProjects(related);
    } catch (error) {
      console.error('Erro ao carregar projeto:', error);
      navigate('/projetos');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'Em Andamento': 'primary',
      'Concluído': 'success',
      'Planejado': 'warning',
    };
    return statusMap[status] || 'secondary';
  };

  const getProjectImage = (proj: Project): string => {
    if (proj.galeriaFotos?.fotos?.length) {
      const mainPhoto = proj.galeriaFotos.fotos.find(f => f.isPrincipal) || proj.galeriaFotos.fotos[0];
      return mainPhoto.url;
    }
    return 'https://via.placeholder.com/400x300?text=Projeto';
  };

  const truncateText = (text: string, maxLength: number = 100): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const fotos = project?.galeriaFotos?.fotos || [];

  const startTimer = useCallback((len: number) => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    if (len <= 1) return;
    autoplayRef.current = setInterval(() => setActiveSlide(i => (i + 1) % len), 4000);
  }, []);

  const stopTimer = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
  }, []);

  useEffect(() => {
    startTimer(fotos.length);
    return () => { if (autoplayRef.current) clearInterval(autoplayRef.current); };
  }, [fotos.length, startTimer]);

  const prevSlide = useCallback(() => {
    setActiveSlide(i => (i === 0 ? fotos.length - 1 : i - 1));
    startTimer(fotos.length);
  }, [fotos.length, startTimer]);

  const nextSlide = useCallback(() => {
    setActiveSlide(i => (i === fotos.length - 1 ? 0 : i + 1));
    startTimer(fotos.length);
  }, [fotos.length, startTimer]);

  const goToSlide = useCallback((index: number) => {
    setActiveSlide(index);
    startTimer(fotos.length);
  }, [fotos.length, startTimer]);

  if (loading) {
    return (
      <div className="page-container">
        <Container className="py-5">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="page-container">
        <Container className="py-5">
          <div className="text-center py-5">
            <i className="bi bi-folder-x" style={{ fontSize: '4rem', color: 'var(--color-gray)' }}></i>
            <p className="text-muted mt-3 fs-5">Projeto não encontrado.</p>
            <Link to="/projetos" className="btn btn-primary mt-3">
              Voltar para projetos
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  const projectImage = getProjectImage(project);
  const projectJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: project.titulo,
    description: project.descricao ? project.descricao.slice(0, 160).trim() : `${project.titulo} - Projeto da Agricampanha`,
    image: projectImage.startsWith('http') ? projectImage : `https://www.agricampanha.agr.br${projectImage}`,
    datePublished: project.dataProjeto,
    dateModified: project.dataProjeto,
    url: `https://www.agricampanha.agr.br/projetos/${project.slug}`,
    author: { '@type': 'Organization', name: 'Agricampanha', url: 'https://www.agricampanha.agr.br' },
    publisher: {
      '@type': 'Organization',
      name: 'Agricampanha',
      logo: { '@type': 'ImageObject', url: 'https://www.agricampanha.agr.br/img/logo.png' },
    },
  };

  return (
    <div className="page-container">
      <PageMeta
        title={project.titulo}
        description={project.descricao ? project.descricao.slice(0, 160).trim() : `${project.titulo} - Projeto da Agricampanha`}
        image={projectImage}
        url={`/projetos/${project.slug}`}
        jsonLd={projectJsonLd}
      />
      {/* Topo padrão */}
      <div className="page-header page-header--compact">
        <div className="page-header-bg" style={{ backgroundImage: "url('/img/hero-image.jpg')" }} />
        <div className="page-header-overlay" />
        <Container className="page-header-content page-header-content--left">
          <span className="section-badge section-badge--solid">{project.categoriaLabel || 'Projetos'}</span>
          <h1>{project.titulo}</h1>
        </Container>
        <div className="page-header-fita" style={{ backgroundImage: "url('/img/bg-fita.png')" }} />
      </div>

      <Container className="py-5">
        <Row>
          <Col lg={12}>
            <article>
              {/* Carrossel de fotos */}
              {fotos.length > 0 && (
                <div
                  className="proj-carousel-wrapper"
                  onMouseEnter={stopTimer}
                  onMouseLeave={() => startTimer(fotos.length)}
                >
                  {/* Slides */}
                  <div className="proj-carousel">
                    {fotos.map((foto, index) => (
                      <div
                        key={foto.id}
                        className={`proj-carousel-slide ${index === activeSlide ? 'proj-carousel-slide--active' : ''} ${touchZoomed && index === activeSlide ? 'proj-carousel-slide--zoomed' : ''}`}
                        style={{ backgroundImage: `url('${foto.url}')` }}
                        role="img"
                        aria-label={foto.legenda || `Foto ${index + 1}`}
                        onTouchStart={() => setTouchZoomed(true)}
                        onTouchEnd={() => setTouchZoomed(false)}
                      />
                    ))}
                  </div>

                  {/* Setas e dots — fora do carousel para ficarem na barra inferior no mobile */}
                  {fotos.length > 1 && (
                    <>
                      <button className="proj-carousel-arrow proj-carousel-arrow--prev" onClick={prevSlide} aria-label="Anterior">
                        <i className="bi bi-chevron-left"></i>
                      </button>
                      <button className="proj-carousel-arrow proj-carousel-arrow--next" onClick={nextSlide} aria-label="Próxima">
                        <i className="bi bi-chevron-right"></i>
                      </button>

                      <div className="proj-carousel-dots">
                        {fotos.map((_, index) => (
                          <button
                            key={index}
                            className={`proj-carousel-dot ${index === activeSlide ? 'proj-carousel-dot--active' : ''}`}
                            onClick={() => goToSlide(index)}
                            aria-label={`Ir para foto ${index + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Descrição */}
              <div className="mb-5">
                <h3 className="h4 mb-3">Sobre o Projeto</h3>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '1.1rem' }}>
                  {project.descricao}
                </div>
              </div>

              {/* Botão voltar */}
              <div className="mb-5">
                <Link to="/projetos" className="btn btn-outline-primary">
                  <i className="bi bi-arrow-left me-2"></i>
                  Voltar para projetos
                </Link>
              </div>
            </article>
          </Col>
        </Row>

        {/* Projetos Relacionados */}
        {relatedProjects.length > 0 && (
          <section className="mt-5 pt-5 border-top">
            <h3 className="mb-4">Outros Projetos</h3>
            <Row className="g-4 projects-row-5">
              {relatedProjects.map((relatedProject) => (
                <Col key={relatedProject.id} xs={12}>
                  <Link to={`/projetos/${relatedProject.slug}`} className="project-card-link">
                    <div className="project-card">
                      <div
                        className="project-img-wrapper"
                        style={{ backgroundImage: `url(${getProjectImage(relatedProject)})` }}
                        role="img"
                        aria-label={relatedProject.titulo}
                      >
                        <span className="project-card-category">
                          {relatedProject.categoriaLabel || relatedProject.status}
                        </span>
                      </div>
                      <div className="project-card-body">
                        <h3 className="project-card-title">{relatedProject.titulo}</h3>
                        <span className="project-read-more">
                          Saiba mais <span className="arrow">→</span>
                        </span>
                      </div>
                    </div>
                  </Link>
                </Col>
              ))}
            </Row>
          </section>
        )}
      </Container>
    </div>
  );
};

export default ProjectDetail;
